export interface ExposureResult {
  meanLum: number;
  shadowClipRatio: number;
  highlightClipRatio: number;
  spread: number;
  score: number;
}

const NUM_BUCKETS = 8;
const BUCKET_SIZE = 256 / NUM_BUCKETS; // 32 luma levels per bucket

/**
 * Histogram-based exposure analysis. Detects shadow/highlight clipping and
 * scores overall exposure quality (0 = unusable, 1 = well-exposed).
 */
export function analyzeExposure(lums: Float32Array): ExposureResult {
  const count = lums.length;
  const histogram = new Float32Array(NUM_BUCKETS);
  let sumLum = 0,
    sumLumSq = 0;

  for (let i = 0; i < count; i++) {
    const lum = lums[i];
    sumLum += lum;
    sumLumSq += lum * lum;
    histogram[Math.min(NUM_BUCKETS - 1, Math.floor(lum / BUCKET_SIZE))]++;
  }

  const meanLum = sumLum / count;
  const spread = Math.sqrt(Math.max(0, sumLumSq / count - meanLum * meanLum));
  const shadowClipRatio = histogram[0] / count;
  const highlightClipRatio = histogram[NUM_BUCKETS - 1] / count;

  // Mean should land in 80–200 for a well-lit passport page.
  const meanScore =
    Math.min(1, Math.max(0, (meanLum - 60) / 50)) *
    Math.min(1, Math.max(0, (220 - meanLum) / 50));
  const spreadScore = Math.min(1, spread / 30);
  const shadowPenalty = Math.min(1, shadowClipRatio / 0.15);
  const highlightPenalty = Math.min(1, highlightClipRatio / 0.15);

  const score = Math.max(
    0,
    meanScore * 0.5 +
      spreadScore * 0.2 -
      shadowPenalty * 0.15 -
      highlightPenalty * 0.15,
  );

  return { meanLum, shadowClipRatio, highlightClipRatio, spread, score };
}
